import MarkdownIt from 'markdown-it'
import markdownItMermaid from './markdownItMermaid'
import markdownItTextMath from 'markdown-it-texmath'
import katex from "katex"
import "katex/contrib/mhchem"
import markdownItHighlightjs from 'markdown-it-highlightjs'
import hljs from 'highlight.js'
import type { Source } from '@/services/types'
import {
  citationIdCharacterClass,
  createCitationPlugin,
  CitationProviderImpl,
  type MarkdownCitation,
} from './markdownCitations'

type MarkdownPlugin = (md: MarkdownIt) => void
type MarkdownPluginWithOptions<TOptions> = (md: MarkdownIt, options: TOptions) => void

function safePlugin(plugin: MarkdownPlugin): MarkdownPlugin
function safePlugin<TOptions>(plugin: MarkdownPluginWithOptions<TOptions>, options: TOptions): MarkdownPlugin
function safePlugin(plugin: MarkdownPlugin | MarkdownPluginWithOptions<unknown>, options?: unknown): MarkdownPlugin {
  return (md: MarkdownIt) => {
    try {
      if (options !== undefined) {
        (plugin as MarkdownPluginWithOptions<unknown>)(md, options)
      } else {
        (plugin as MarkdownPlugin)(md)
      }
    } catch {
      // Plugin registration failed, continue without it
    }
  }
}

// Safe highlight function that won't throw
const safeHljs = {
  ...hljs,
  highlight: (code: string, options: { language: string }) => {
    try {
      return hljs.highlight(code, options)
    } catch {
      return { value: code }
    }
  },
  highlightAuto: (code: string) => {
    try {
      return hljs.highlightAuto(code)
    } catch {
      return { value: code }
    }
  },
} as typeof hljs

const texmath = markdownItTextMath as typeof markdownItTextMath & {
  rules: Record<string, {
    inline: Array<Record<string, unknown>>
    block?: Array<Record<string, unknown>>
  }>
}

const bracketsRules = texmath.rules.brackets!
const gitlabRules = texmath.rules.gitlab!
// Add a rule to allow block brackets "inline" as in a list
texmath.rules.brackets_plus = {
  ...bracketsRules,
  inline: [
    {
      name: 'math_inline_brackets_display',
      rex: /\\\[([\s\S]+?)\\\]/gy,
      tmpl: '<section><eqn>$1</eqn></section>',
      tag: '\\[',
      displayMode: true,
    },
    ...bracketsRules.inline,
  ],
}
texmath.rules.gitlab = {
  ...gitlabRules,
  block: [
    {
      name: 'math_block_eqno',
      rex: /`{3}(?:math|latex|tex)\s*([^`]+?)\s*?`{3}\s*\(([^)\r\n]+?)\)/gm,
      tmpl: '<section class="eqno"><eqn>$1</eqn><span>($2)</span></section>',
      tag: '```',
    },
    {
      name: 'math_block',
      rex: /`{3}(?:math|latex|tex)\s*([^`]*?)\s*`{3}/gm,
      tmpl: '<section><eqn>$1</eqn></section>',
      tag: '```',
    },
  ],
}

export const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
})

markdown.use(safePlugin(texmath, {
  engine: katex,
  delimiters: ['dollars', 'brackets_plus', 'gitlab', 'beg_end'],
}))
markdown.use(safePlugin(markdownItHighlightjs, { hljs: safeHljs }))
markdown.use(safePlugin(markdownItMermaid))
markdown.use(createCitationPlugin())

const originalLinkOpen = markdown.renderer.rules.link_open
markdown.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]
  const href = token?.attrGet('href') ?? ''

  if (token && href && !href.startsWith('#')) {
    token.attrSet('target', '_blank')
    token.attrSet('rel', 'noopener noreferrer')
  }

  if (originalLinkOpen) {
    return originalLinkOpen(tokens, idx, options, env, self)
  }
  return self.renderToken(tokens, idx, options)
}

// Wrap fence renderer to catch any highlighting errors
const originalFence = markdown.renderer.rules.fence
if (originalFence) {
  markdown.renderer.rules.fence = (tokens, idx, options, env, self) => {
    try {
      return originalFence(tokens, idx, options, env, self)
    } catch {
      const token = tokens[idx]
      const escaped = (token?.content || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return `<pre><code>${escaped}</code></pre>`
    }
  }
}

export interface RenderMarkdownOptions {
  sources?: Source[]
  footnotePrefix?: string
}

export interface RenderedMarkdown {
  html: string
  citations: MarkdownCitation[]
}

function normalizeCitationSyntax(text: string): string {
  // Remove trailing spaces before citation references (prevents soft breaks).
  // Exclude footnote definitions [^id]:
  const withoutCitationSoftBreaks = text.replace(
    new RegExp(`[ \\t]+\\n(\\[\\^(${citationIdCharacterClass})\\])(?!:)`, 'g'),
    '$1',
  )

  // Ensure linkify doesn't absorb trailing [^citation] into the URL token.
  return withoutCitationSoftBreaks.replace(
    /((?:https?:\/\/|www\.)[^\s<]+?)(\[\^[^\]\s]+\](?:[ \t]*\[\^[^\]\s]+\])*)(?=(?:\s|$))/g,
    '$1\u00A0$2',
  )
}

export function renderMarkdown(input: string, options: RenderMarkdownOptions = {}): RenderedMarkdown {
  const cleanedInput = normalizeCitationSyntax(input)
  const provider = new CitationProviderImpl({
    sources: options.sources,
    footnotePrefix: options.footnotePrefix,
  })

  const env = {
    citationContext: {
      provider,
    },
  }

  try {
    const html = markdown.render(cleanedInput, env)
    return {
      html,
      citations: provider.getCitations(),
    }
  } catch {
    // Last resort: escape and return as preformatted text
    const escaped = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return {
      html: `<pre>${escaped}</pre>`,
      citations: [],
    }
  }
}

export type { MarkdownCitation } from './markdownCitations'
